import { useEffect } from 'react';
import useSWR from 'swr';
import { useQueryPanelStore } from '@/stores/query-panel.store';
import { useSourcesStore } from '@/stores/sources.store';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import type { SourcesSelection } from '@/types/source';
import type { SourceFolderChildren } from '@/types/source-folder';

export function useSyncQueryPanelFolderScope(selection: SourcesSelection) {
    const setSelectedSources = useQueryPanelStore((s) => s.setSelectedSources);
    const clearSourceSelection = useQueryPanelStore(
        (s) => s.clearSourceSelection,
    );
    const addSources = useSourcesStore((s) => s.addSources);

    const folderId = selection.kind === 'folder' ? selection.id : null;

    const { data } = useSWR<SourceFolderChildren>(
        folderId
            ? [CACHE_KEYS.SOURCE_FOLDER_CHILDREN, folderId, '', 'all']
            : null,
        ([, id]: [string, string]) => EngineService.getSourceFolderChildren(id),
        { revalidateOnFocus: false },
    );

    const folderSourceIdsKey = data?.sources.map((s) => s.id).join(',');

    useEffect(() => {
        if (data?.sources.length) {
            addSources(data.sources);
        }
    }, [data, addSources]);

    useEffect(() => {
        if (!folderId) {
            clearSourceSelection();
            return;
        }
        if (folderSourceIdsKey === undefined) {
            clearSourceSelection();
            return;
        }

        const memberIds = folderSourceIdsKey
            ? folderSourceIdsKey.split(',')
            : [];
        setSelectedSources(
            new Set(memberIds),
            memberIds.length > 0 ? new Map([[folderId, memberIds]]) : undefined,
        );
    }, [
        folderId,
        folderSourceIdsKey,
        setSelectedSources,
        clearSourceSelection,
    ]);
}
